export default {
  // Page titles
  title: 'Meters',
  meter_readings: 'Meter Readings',
  bulk_readings: 'Bulk Meter Readings',

  // EP-58 T5 — condominium accounts have no per-unit meters
  not_applicable_condo_title: 'Meters do not apply to condominium accounts',
  not_applicable_condo_body:
    'Condominiums share building-level meters; readings are not tracked per unit. Common area fees cover the recurring charges instead.',

  // Descriptions
  description: 'Track utility meter readings',
  bulk_description: 'Record readings for multiple meters at once',
  bulk_record_description: 'Record {{type}} meter readings for {{property}}',

  // Labels
  labels: {
    // Appended to a bulk-readings room type heading when the room type has a
    // floor. A floor already written with its own word ("ชั้น3") is shown as is.
    floor_value: 'Floor {{floor}}',
    meter: 'Meter',
    meter_type: 'Meter Type',
    used_in_invoices: 'Used in invoices',
    serial_number: 'Serial Number',
    serial_number_optional: 'Serial Number (optional)',
    latest_reading: 'Latest Reading',
    latest: 'Latest',
    latest_usage: 'Latest Usage',
    previous_reading: 'Previous Reading',
    current_reading: 'Current Reading',
    reading_date: 'Reading Date',
    reading_month: 'Reading Month',
    reading_value: 'Reading Value',
    usage: 'Usage',
    photo: 'Photo',
    reading_history: 'Reading History',
    billing_day: 'Billing day: {{day}}',
    unit: 'Unit',
    serial_no: 'Serial No.',
    previous: 'Previous',
    current: 'Current',
    meter_type_label: 'Meter Type:',
    reading_month_label: 'Reading Month:',
  },

  // Placeholders
  placeholders: {
    serial_number: 'e.g., WM-001',
    previous_value: 'Previous meter value',
    current_value: 'Current meter value',
  },

  // Meter types
  types: {
    water: 'Water',
    electricity: 'Electricity',
  },

  // Embedded meters section on the unit-create form
  section: {
    title: 'Meters',
    toggle_label: 'Set up meters for this unit',
    water: 'Water',
    electricity: 'Electricity',
    serial_optional: 'Serial number (optional)',
    opening_reading_placeholder: 'Current meter value',
    opening_reading_hint: "Enter the value shown on the meter right now. We'll use it as the starting point. Next month's invoice will charge the difference.",
  },

  // Bulk readings
  bulk: {
    title: 'Bulk Meter Readings',
    property_label: 'Property',
    select_property: 'Select a property',
    reading_date: 'Reading Date',
    previous: 'Previous',
    current: 'Current',
    save_all: 'Save All Readings',
    save_readings: 'Save Readings',
    skip_unchanged: 'Skip Unchanged',
    confirm_title: 'Confirm Save Readings',
    confirm_summary: '{{created}} readings will be created, {{edited}} readings will be edited',
    more_rows: '+{{n}} more',
  },

  rollover: {
    check_on_save: 'Save to review and confirm the rollover usage.',
    badge: 'Rolled over: {{usage}} units',
    confirm: 'I confirm this reading is correct (meter rolled over)',
    unconfirmed_skipped: 'Unconfirmed rollover readings will not be saved.',
    needs_confirmation: 'Review and confirm the rollover readings before saving them again.',
  },

  // Actions
  actions: {
    add_meter: 'Add Meter',
    edit_meter: 'Edit Meter',
    delete_meter: 'Delete Meter',
    add_reading: 'Record Reading',
    edit_reading: 'Edit Reading',
    save_reading: 'Save Reading',
    view_photos: 'View Photos',
    // Short label for the compact per-row "record a reading" table button
    // (Dashboard meter readings due table) — add_reading is too long there.
    record_short: 'Record',
  },

  // Empty states
  empty: {
    no_meters: 'No meters',
    no_meters_description: 'Add meters to track utility usage.',
    no_readings: 'No readings recorded',
    no_readings_description: 'Start recording meter readings to track usage.',
    need_two_readings: 'Need 2+ readings',
    no_serial_number: 'No serial number',
    no_type_meters: 'No {{type}} meters found',
    no_type_meters_cta: 'Add a meter to a unit to start recording readings.',
  },

  // B-057 — inline meter provisioning from the readings page
  add_meter_modal: {
    title: 'Add {{type}} meter',
    description: 'Select a unit that has no {{type}} meter yet. Adding one lets you record its readings.',
    add_for_type: 'Add {{type}}',
    added: '{{type}} meter added',
    error: 'Could not add the {{type}} meter',
    all_have_meters: 'Every unit already has a {{type}} meter.',
    no_units: 'This property has no units yet. Add a unit first, then come back to add its meter.',
    more_units_hint: 'Showing the first page of units. Units already listed above are excluded.',
    open: 'Add meter',
  },

  // Setup guide (unit detail - meters section)
  setup_guide: {
    title: 'Set Up Meters for This Unit',
    description:
      'Meters track water and electricity usage for billing. Add meters to start recording readings.',
    step_1_title: 'Add a water meter',
    step_1_description: 'Track water usage with an optional serial number for identification.',
    step_2_title: 'Add an electricity meter',
    step_2_description: 'Track electricity usage for accurate billing each month.',
    step_3_title: 'Record readings via Bulk Readings',
    step_3_description:
      'Go to the property page and use Bulk Readings to record all meters at once, organized by room type.',
  },

  // Photo
  photo: {
    title: 'Reading Photos',
    evidence: 'Photo Evidence (optional)',
    hint: 'Take a photo of the meter reading',
    reading_photo_alt: 'Reading photo {{count}}',
  },

  // Validation
  validation: {
    meter_type_required: 'Meter type is required',
  },

  // List page
  list_page: {
    title: 'Meter Readings',
    description: 'View and record meter readings for your properties',
    select_property: 'Select property',
    all_properties: 'All Properties',
    empty_title: 'No meters found',
    empty_description: 'This property has no meters configured',
    needs_entry: 'Needs entry',
    recorded: 'Recorded',
    water_section: 'Water',
    electricity_section: 'Electricity',
    no_property_selected: 'Select a property to view meters',
  },

  // Unit readings form (wizard step 6 / single-unit reading surface)
  unit_readings: {
    title: 'Record Readings',
    save: 'Save Readings',
    // EP-54: split into i18next v25 plural forms; base key removed.
    consumption_this_month_one: 'Consumption this month: {{count}} unit',
    consumption_this_month_other: 'Consumption this month: {{count}} units',
    no_meters: 'No meters configured for this unit.',
    saved: 'Readings saved successfully',
    error_prefix: '{{meter}}: {{message}}',
  },

  // Messages
  messages: {
    unit_created_meter_failed: 'Unit created, but {{meter_type}} meter setup failed: {{error}}',
    delete_title: 'Delete Reading',
    delete_confirm: 'Delete this reading? This action cannot be undone.',
    delete_meter_confirm: 'Delete this meter? This action cannot be undone.',
    created: 'Reading recorded successfully',
    updated: 'Reading updated successfully',
    deleted: 'Reading deleted successfully',
    bulk_saved: 'All readings saved successfully',
    readings_saved: '{{count}} readings saved successfully',
    no_changes: 'No changes to save',
    save_failed: 'Failed to save readings',
    // Legacy single-shot lock message — retained for back-compat with any
    // older surfaces still consuming it. The new bulk save flow uses the
    // partial_save_* / invoice_locked_title / locked_inline_edit_cta keys
    // below.
    invoice_locked: 'Meters for unit(s) {{units}} are referenced by invoices. Click "Edit individually" (✏️) at the end of each row to edit that meter on its own page.',
    all_properties_read_only_hint: 'All Properties view is read-only. Select a property above to record or edit meter readings.',
    edit_individually: 'Edit individually',
    not_found: 'Meter not found',
    partial_failures_one: '{{count}} property failed to load. Others rendered below',
    partial_failures_other: '{{count}} properties failed to load. Others rendered below',

    // Partial-success bulk save (server returns 200 with saved / locked /
    // no_op / cascade_blocked arrays — see useBulkReadings BulkSaveResult).
    partial_save_summary_title: 'Some readings need attention',
    partial_save_summary_body: 'Saved {{saved}} reading(s). {{locked}} were locked by existing invoices.',
    no_op_skipped_one: '{{count}} row skipped. Value matches what is already on file.',
    no_op_skipped_other: '{{count}} rows skipped. Values match what is already on file.',
    invoice_locked_title: 'Locked by invoices',
    locked_inline_edit_cta: 'Edit individually',
    cascade_blocked_warning_one: '{{count}} reading was not saved because changing it would corrupt usage on a later invoice that is already billed. Open the affected meter individually to resolve.',
    cascade_blocked_warning_other: '{{count}} readings were not saved because changing them would corrupt usage on later invoices that are already billed. Open the affected meters individually to resolve.',
  },
}
